import { CustomDatePicker } from "@/components/custom-date-picker";
import CustomTable from "@/components/CustomTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { formatType } from "@/lib/helper";
import { cn } from "@/lib/utils";
import { getAllUser } from "@/services/Users.service";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { IoFunnelOutline } from "react-icons/io5";
import { columns } from "./columns";
import ExportFilterAction from "@/components/ExportFilterAction";
import {
  getAllBlockedUsers,
  getAllUnverifiedUsers,
  getAllUserCount,
  getAllVerifiedUsers,
} from "@/services/Home.service";
import CustomCard from "@/components/CustomCard";

const Users = () => {
  const q = useQueryClient();
  const [filter, setFilter] = useState({
    search: "",
    from: "",
    to: "",
    status: "",
  });
  const [dateTo, setDateTo] = useState<Date>();
  const [dateFrom, setDateFrom] = useState<Date>();
  const [showFilterNumber, setShowFilterNumber] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);

  const userCountRes = useQuery({
    queryKey: ["userCount"],
    queryFn: async () =>
      await getAllUserCount({ from: filter?.from, to: filter?.to }),
    refetchOnMount: "always",
  });
  const res = useQuery({
    queryKey: ["verifiedUsers"],
    queryFn: async () =>
      await getAllVerifiedUsers({ from: filter?.from, to: filter?.to }),
    refetchOnMount: "always",
  });
  const unverified = useQuery({
    queryKey: ["unverifiedUsers"],
    queryFn: async () =>
      await getAllUnverifiedUsers({ from: filter?.from, to: filter?.to }),
    refetchOnMount: "always",
  });

  // const active = useQuery({
  //   queryKey: ["activeUsers"],
  //   queryFn: async () =>
  //     await getAllActiveUsers({ from: filter.from, to: filter.to }),
  //   refetchOnMount: "always",
  // });
  const blocked = useQuery({
    queryKey: ["blockedUsers"],
    queryFn: async () =>
      await getAllBlockedUsers({ from: filter?.from, to: filter?.to }),
    refetchOnMount: "always",
  });

  const { data, isLoading } = useInfiniteQuery({
    queryKey: ["users", search, page],
    queryFn: async ({ pageParam = page }) =>
      await getAllUser(pageParam, filter, search),
    getNextPageParam: (lastPage: [], pages: []) => {
      if (lastPage.length === 0) return undefined;
      return pages.length + 1;
    },
    placeholderData: keepPreviousData,
  });

  const dataArr = data?.pages?.map((page) => page?.data?.data?.data).flat();
  const meta = data?.pages?.map((page) => page?.data?.data?.meta).flat()[0];

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSelectToChange = (dateString: Date) => {
    setShowFilterNumber(showFilterNumber + 1);
    setDateTo(dateString);
    setFilter({
      ...filter,
      to: formatType(dateString),
    });
  };
  const handleSelectFromChange = (dateString: Date) => {
    setShowFilterNumber(showFilterNumber + 1);
    setDateFrom(dateString);
    setFilter({
      ...filter,
      from: formatType(dateString),
    });
  };

  const handleSelectStatusChange = (e: string) => {
    setShowFilterNumber(showFilterNumber + 1);
    setFilter({
      ...filter,
      status: e,
    });
  };

  const reset = () => {
    setFilter({
      from: "",
      to: "",
      search: "",
      status: "",
    });
    q.clear();
  };

  return (
    <div className="grid py-8 p-6 gap-[40px]">
      <div className="grid items-center gap-4">
        <div className="flex-col gap-2">
          <p className="text-2xl font-medium text-primary-3">Customers</p>
          <p className="text-base text-gray-80">
            All Treasura Express Customers are listed here
          </p>
        </div>
        <div className="grid gap-6 lg:grid-cols-4">
          <CustomCard
            title="Total Customers"
            mainTitle={userCountRes?.data?.data?.data?.count}
            isLoading={userCountRes?.isFetching}
            date={filter.to}
          />
          <CustomCard
            title="Verified customers"
            mainTitle={res?.data?.data?.data?.count}
            isLoading={res?.isFetching}
            date={filter.to}
          />
          <CustomCard
            title="Unverified customers"
            mainTitle={unverified?.data?.data?.data?.count}
            isLoading={unverified?.isFetching}
            date={filter.to}
          />
          <CustomCard
            title="Suspended customers"
            mainTitle={blocked?.data?.data?.data?.count}
            isLoading={blocked?.isFetching}
            date={filter.to}
          />
        </div>
      </div>

      <div className="grid w-full gap-8">
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <Input
              placeholder="Search for customers by name, email or user ID"
              value={showFilter ? filter?.search : (search ?? "")}
              onChange={(event) => {
                if (showFilter) {
                  setFilter({
                    ...filter,
                    search: event.target.value,
                  });
                } else {
                  setSearch(event.target.value);
                }
              }}
              className={cn(
                "max-w-sm ",
                showFilter ? "bg-transparent" : "bg-off-white-1",
              )}
            />
            <div className="flex flex-wrap gap-4 ml-auto">
              <Button
                variant="outline"
                className="border-off-white text-primary0"
                onClick={() => {
                  setShowFilter(!showFilter);
                  setSearch("");
                  reset();
                }}
              >
                <IoFunnelOutline />
                {showFilter ? "Hide Filters" : "Show Filters"}
              </Button>
              <ExportFilterAction />
            </div>
          </div>

          <div
            className={cn(
              "flex flex-col gap-4 justify-between w-[750px]",
              showFilter ? "block" : "hidden",
            )}
          >
            <div className="flex gap-4 items-center flex-wrap">
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Start date</p>
                <CustomDatePicker
                  date={dateFrom}
                  setDate={handleSelectFromChange}
                />
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">End date</p>
                <CustomDatePicker
                  date={dateTo}
                  setDate={handleSelectToChange}
                />
              </div>

              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Status</p>

                <Select
                  onValueChange={handleSelectStatusChange}
                  value={filter?.status}
                >
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {[
                      {
                        name: "Suspended",
                        value: "suspended",
                      },
                      {
                        name: "Active",
                        value: "active",
                      },
                    ]?.map((item, index) => (
                      <SelectItem key={item?.name + index} value={item?.value}>
                        {item?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">KYC Status</p>

                <Select>
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="Select KYC status" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {[
                      {
                        name: "Verified",
                        value: "verified",
                      },
                      {
                        name: "Unverified",
                        value: "unverified",
                      },
                    ]?.map((item, index) => (
                      <SelectItem key={item?.name + index} value={item?.value}>
                        {item?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Payout Status</p>

                <Select>
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="Select Payout Status" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {[
                      {
                        name: "True",
                        value: "true",
                      },
                      {
                        name: "False",
                        value: "false",
                      },
                    ]?.map((item, index) => (
                      <SelectItem key={item?.name + index} value={item?.value}>
                        {item?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Exempt Status</p>

                <Select>
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="Select Exempt status" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {[
                      {
                        name: "True",
                        value: "true",
                      },
                      {
                        name: "False",
                        value: "false",
                      },
                    ]?.map((item, index) => (
                      <SelectItem key={item?.name + index} value={item?.value}>
                        {item?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <Button
                onClick={() => q.refetchQueries()}
                className=" text-sm font-medium "
              >
                Apply
              </Button>
              <Button
                variant="outline"
                type="button"
                className=" text-sm font-medium text-primary"
                onClick={reset}
              >
                Reset filters
              </Button>
            </div>
          </div>
        </div>

        <CustomTable
          columns={columns}
          data={dataArr ?? []}
          meta={meta}
          isLoading={isLoading}
          handlePageChange={handlePageChange}
          showPagination
        />
      </div>
    </div>
  );
};

export default Users;
